import { useEffect, useState } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Inbox, CalendarCheck, Tag, Sun, Image, BarChart3, LogOut, DollarSign, TrendingUp
} from 'lucide-react';
import royalLogo from '@/assets/royal-logo-clean.png';
import { supabase } from '@/integrations/supabase/client';

export default function AdminLayout() {
  const { isAdmin, loading, signOut, user } = useAdmin();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ enquiries: 0, bookings: 0, revenue: 0, pending: 0, depositsReceived: 0, remainingBalance: 0 });

  const navItems = [
    { to: '/admin/enquiries', icon: Inbox, label: t('enquiries') },
    { to: '/admin/bookings', icon: CalendarCheck, label: t('bookings') },
    { to: '/admin/seasons', icon: Sun, label: t('seasonsPricing') },
    { to: '/admin/coupons', icon: Tag, label: t('coupons') },
    { to: '/admin/photos', icon: Image, label: t('photos') },
    { to: '/admin/revenue', icon: BarChart3, label: t('revenue') },
  ];

  useEffect(() => {
    if (!loading && !isAdmin) navigate('/admin/login');
  }, [loading, isAdmin, navigate]);

  const loadStats = async () => {
    const [enq, book] = await Promise.all([
      supabase.from('enquiries').select('id, status', { count: 'exact' }),
      supabase.from('bookings').select('id, total_amount, deposit_amount, remaining_balance, payment_status'),
    ]);
    const pending = enq.data?.filter(e => e.status === 'pending').length ?? 0;
    const revenue = book.data?.reduce((s, b) => s + Number(b.total_amount || 0), 0) ?? 0;
    const depositsReceived = book.data?.reduce((s, b) => s + Number(b.deposit_amount || 0), 0) ?? 0;
    const remainingBalance = book.data?.reduce((s, b) => s + Number(b.remaining_balance || 0), 0) ?? 0;
    setStats({
      enquiries: enq.count ?? 0,
      bookings: book.data?.length ?? 0,
      revenue,
      pending,
      depositsReceived,
      remainingBalance,
    });
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadStats();

    const channel = supabase
      .channel('admin-stats')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => loadStats())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'enquiries' }, () => loadStats())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse font-serif text-2xl text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const statCards = [
    { label: t('pendingEnquiries'), value: stats.pending, icon: Inbox, color: 'text-amber-600' },
    { label: t('totalBookings'), value: stats.bookings, icon: CalendarCheck, color: 'text-accent' },
    { label: t('totalRevenue'), value: `€${stats.revenue.toLocaleString()}`, icon: DollarSign, color: 'text-green-600' },
    { label: t('depositsReceived'), value: `€${stats.depositsReceived.toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600' },
    { label: t('remainingBalance'), value: `€${stats.remainingBalance.toLocaleString()}`, icon: DollarSign, color: 'text-orange-600' },
    { label: t('totalEnquiries'), value: stats.enquiries, icon: TrendingUp, color: 'text-muted-foreground' },
  ];

  return (
    <div className="min-h-screen flex bg-secondary/30">
      <aside className="w-64 bg-card border-r border-border flex flex-col min-h-screen sticky top-0">
        <div className="p-6 border-b border-border">
          <img src={royalLogo} alt="Sivota Royal" className="h-10 mb-2" />
          <p className="text-xs text-muted-foreground tracking-wider uppercase">{t('adminDashboard')}</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <p className="text-xs text-muted-foreground truncate mb-3">{user?.email}</p>
          <button
            onClick={() => { signOut(); navigate('/admin/login'); }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
          >
            <LogOut size={16} />
            {t('signOut')}
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-light mb-6">{t('dashboardOverview')}</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {statCards.map(c => (
              <div key={c.label} className="bg-card border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <c.icon size={18} className={c.color} />
                </div>
                <p className="font-serif text-2xl font-light">{c.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
